import { IconButton } from "@mui/material"
import { UploadFileOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import { AccountsPayableTransactionTypeChip } from "../components/AccountsPayable/AccountsPayableTransactionTypeChip"
import {
  formatAccountsPayableAmount,
  formatAccountsPayableBalance,
  toSignedAccountsPayableAmount,
} from "../utils/accountsPayableFormat"

import { DATE_FORMAT } from "@/constants/formats"
import type { VendorApLedgerEntry } from "@/graphql/codegen/graphql"
import { formatDate } from "@/utils/date"
import { classNames } from "@/utils/utils"

interface UseAccountsPayableTransactionColumnsProps {
  showBalanceColumn: boolean
  onDocumentsOpen?: (entry: VendorApLedgerEntry) => void
}

export const useAccountsPayableTransactionColumns = ({
  showBalanceColumn,
  onDocumentsOpen,
}: UseAccountsPayableTransactionColumnsProps): ColumnDef<VendorApLedgerEntry>[] => {
  return useMemo<ColumnDef<VendorApLedgerEntry>[]>(() => {
    const columns: ColumnDef<VendorApLedgerEntry>[] = [
      {
        accessorKey: "transactionDate",
        header: "Transaction Date",
        cell: ({ row }) =>
          row.original.transactionDate ? formatDate(row.original.transactionDate, DATE_FORMAT.ISO_18601) : null,
        enableSorting: false,
        size: 150,
      },
      {
        // AK-5105 AC5: the Transaction ID is the GL transaction reference (e.g. GL-2026-0118), as on the GL
        // Transactions tab; each ledger entry maps 1:1 to a GL transaction. AK-7122: the previous `externalSystemId`
        // is an AS400-only value that stays empty until AS400 acknowledges the entry.
        accessorKey: "glTransactionReferenceNumber",
        header: "Transaction ID",
        cell: ({ row }) => row.original.glTransactionReferenceNumber ?? null,
        enableSorting: false,
        size: 130,
      },
      {
        accessorKey: "invoiceAction",
        header: "Type",
        cell: ({ row }) => <AccountsPayableTransactionTypeChip invoiceAction={row.original.invoiceAction} />,
        enableSorting: false,
        size: 160,
      },
      {
        id: "description",
        header: "Description",
        cell: ({ row }) => row.original.invoice?.invoiceNumber ?? null,
        enableSorting: false,
        size: 280,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: ({ row }) => {
          const amount = toSignedAccountsPayableAmount(row.original.amount, row.original.type)

          return (
            <span className={classNames({ "text-secondary-600": amount < 0 })}>
              {formatAccountsPayableAmount(amount)}
            </span>
          )
        },
        enableSorting: false,
        meta: { align: "right" },
        size: 130,
      },
    ]

    if (showBalanceColumn) {
      columns.push({
        accessorKey: "apBalance",
        header: "AP Balance",
        cell: ({ row }) => {
          const { apBalance } = row.original
          if (apBalance === null || apBalance === undefined) return null

          return (
            <span className={classNames({ "text-secondary-600": apBalance < 0 })}>
              {formatAccountsPayableBalance(apBalance)}
            </span>
          )
        },
        enableSorting: false,
        meta: { align: "right" },
        size: 130,
      })
    }

    columns.push({
      id: "docs",
      header: "Document Icon",
      size: 96,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.invoice?.hasDocuments ? (
          <IconButton
            size='small'
            onClick={() => onDocumentsOpen?.(row.original)}
            data-testid={`accounts-payable.transaction-table.docs-button-${row.original.vendorApLedgerEntryId}`}
          >
            <UploadFileOutlined fontSize='small' className='text-gray-600' />
          </IconButton>
        ) : null,
    })

    return columns
  }, [showBalanceColumn, onDocumentsOpen])
}
